import styled, { css, keyframes } from 'styled-components';
import _ from 'lodash';
import Text from 'components/Text';
import Icon from 'components/Icon';
import {
  colorVariables,
  fontVariables,
  buttonSizeVariables,
  socialsColor,
  socialsActiveColor,
  socialsHoverColor,
} from 'theme/variables';
import base from './base';
import {
  embedWrapper,
  embedExpandPrice,
  embedExpandQuality,
} from './mediaQueries';
import { isHasDesktopHover } from '../../../utils/isMobile';

export const Tooltip = styled.span`
  position: absolute;
  bottom: 51px;
  left: 50%;
  transform: translate(-50%, 0);
  font-size: ${fontVariables.fs_xxs};
  color: white;
  padding: 12px;
  background-color: rgb(26, 26, 26);
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: 175ms ease-in-out;
  border-radius: 5px;

  &:after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -12px;
    transform: translateX(-50%);
    z-index: 3;
    border-image: initial;
    border: 7px solid transparent;
    border-top-color: ${colorVariables.gray_cod};
  }

  ${({ toolTipBottom }) =>
    toolTipBottom &&
    css`
      top: auto;
      bottom: -50px;

      &:after {
        bottom: auto;
        top: -12px;
        border-top-color: transparent;
        border-bottom-color: ${colorVariables.gray_cod};
      }
    `};

  ${({ toolTipRight }) =>
    toolTipRight &&
    css`
      left: initial;
      right: 0;
      transform: translate(0);

      &:after {
        left: initial;
        right: 20px;
        transform: translate(0);
      }
    `};
`;

export const ButtonStyled = styled.button`
  ${base};
  // styles depends on buttonStyle prop
  ${({ theme, disable, isMobile, buttonStyle }) =>
    buttonStyle === 'background' &&
    css`
      height: 40px;
      color: white;
      background-color: ${_.get(colorVariables, theme.main_bg)};
      border-radius: 3px;
      &: ${isHasDesktopHover ? 'hover' : 'active'} {
        background-color: ${!isMobile && !disable
          ? colorVariables[theme.main_hover_color]
          : colorVariables[theme.main_bg]};
        transition: 135ms ease-out;
      }
      &:active {
        background-color: ${colorVariables[theme.main_hover_color]};
        transition: 135ms ease-out;
      }
    `}
  ${({ buttonStyle }) =>
    buttonStyle === 'transparent' &&
    css`
      background-color: transparent;
    `}
  // styles for disabled button
  ${({ disable }) =>
    disable &&
    css`
      cursor: default;
      pointer-events: none;
      opacity: 0.5;
    `}
  // styles for loading button
  ${({ isLoading }) =>
    isLoading &&
    css`
      cursor: default;
    `}
  // styled depends on size prop
  ${({ size }) =>
    size &&
    css`
      width: ${_.get(buttonSizeVariables, size, null)};
    `};

  ${({ isMobile, disable, buttonStyle, hoverColor, theme }) =>
    !isMobile &&
    !disable &&
    buttonStyle !== 'background' &&
    css`
      &: ${isHasDesktopHover ? 'hover' : 'active'} {
        ${StyledIcon} {
          fill: ${_.get(
            colorVariables,
            hoverColor,
            colorVariables[theme.main_hover_color]
          )};
          transition: 135ms ease-out;
        }
        ${StyledText} {
          color: ${_.get(
            colorVariables,
            hoverColor,
            colorVariables[theme.main_hover_color]
          )};
          transition: 135ms ease-out;
        }
      }
    `};

  ${({ isMobile, disable, buttonStyle, color, theme, hoverColor }) =>
    !isMobile &&
    !disable &&
    buttonStyle === 'transparent' &&
    css`
      &: ${isHasDesktopHover ? 'hover' : 'active'} {
        ${StyledIcon} {
          fill: ${!isMobile && !disable
            ? colorVariables.blue_navyblue
            : _.get(
                colorVariables,
                color,
                colorVariables[theme.main_hover_color]
              )};
          transition: 135ms ease-out;
        }
        ${StyledText} {
          color: ${() => {
            if (!isMobile && !disable) {
              return css`
                ${_.get(
                  colorVariables,
                  hoverColor,
                  colorVariables[theme.main_hover_color]
                )};
              `;
            }

            return css`
              ${_.get(
                colorVariables,
                color,
                colorVariables[theme.main_default]
              )};
            `;
          }};
          transition: 135ms ease-out;
        }
      }
    `};

  ${({ hoverColor, buttonStyle, theme, text, isMobile }) =>
    buttonStyle !== 'background' &&
    isMobile &&
    text &&
    css`
      &:active {
        ${StyledIcon} {
          fill: ${_.get(
            colorVariables,
            hoverColor,
            colorVariables[theme.main_hover_color]
          )};
          transition: 135ms ease-out;
        }
        ${StyledText} {
          color: ${_.get(
            colorVariables,
            hoverColor,
            colorVariables[theme.main_hover_color]
          )};
          transition: 135ms ease-out;
        }
      }
    `};

  ${({ buttonStyle, text, isMobile }) =>
    buttonStyle !== 'background' &&
    isMobile &&
    !text &&
    css`
      &:active {
        ${StyledIcon} {
          transform: scale(1.2);
          opacity: 0.7;
          transition: 135ms ease-out;
        }
      }
    `};

  ${({ buttonStyle, hoverColor, theme }) =>
    buttonStyle === 'transparent' &&
    css`
      &:active {
        ${StyledIcon} {
          fill: ${_.get(
            colorVariables,
            hoverColor,
            colorVariables[theme.main_hover_color]
          )};
          transition: 135ms ease-out;
        }
        ${StyledText} {
          color: ${_.get(
            colorVariables,
            hoverColor,
            colorVariables[theme.main_hover_color]
          )};
          transition: 135ms ease-out;
        }
      }
    `};

  ${({ disable, color, theme }) =>
    disable &&
    css`
      &:active {
        ${StyledIcon} {
          cursor: default;
        }
        ${ButtonStyled} {
          color: ${_.get(
            colorVariables,
            color,
            colorVariables[theme.main_default]
          )};
          cursor: default;
        }
      }
    `};

  ${isHasDesktopHover &&
  `&:hover {
      ${Tooltip} {
        opacity: 1;
        visibility: visible;
      }
    }`}

  ${embedWrapper};
`;

export const SocialWrapper = styled.button`
  ${base};
  flex-direction: column;
  align-items: center;
  width: 48px;
  height: 40px;
  border-radius: 3px;
  transition:
    background-color 135ms ease-in,
    color 135ms ease-in;
  &: ${isHasDesktopHover ? 'hover' : 'active'} {
    transition:
      background-color 135ms ease-out,
      color 135ms ease-out;
  }
  ${({ iconType }) =>
    iconType &&
    css`
      background-color: ${_.get(socialsColor, iconType, null)};
      &: ${isHasDesktopHover ? 'hover' : 'active'} {
        background-color: ${_.get(socialsHoverColor, iconType, null)};
      }
      &:active {
        background-color: ${_.get(socialsActiveColor, iconType, null)};
      }
    `};
`;

export const ContentWrap = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  // styled depends on align props
  ${({ align }) =>
    align === 'left' &&
    css`
      justify-content: flex-start;
    `};
`;

export const StyledText = styled(Text)`
  transform: translate(0, 1px);
  // styles depends on buttonStyle prop
  ${({ buttonStyle }) =>
    buttonStyle === 'background' &&
    css`
      transform: translate(0, 0);
      color: white;
      font-size: ${fontVariables.fs_md};
    `};
  ${({ color, theme, buttonStyle }) =>
    buttonStyle === 'transparent' &&
    css`
      color: ${color || colorVariables[theme.main_default]};
    `};
  ${({ type }) =>
    type === 'uppercase' &&
    css`
      text-transform: uppercase;
    `};
`;

export const StyledIcon = styled(Icon)`
  // hover only when not disabled or mobile
  ${({ separation }) =>
    separation &&
    css`
      margin-right: ${separation}px;
    `};

  ${SocialWrapper}:${isHasDesktopHover ? 'hover' : 'active'} & {
    fill: white;
  }
  // styles depends on buttonStyle prop
  ${({ buttonStyle, separation }) =>
    buttonStyle === 'background' &&
    css`
      margin-right: ${separation ? `${separation}px` : '8px'};
      fill: white;
    `};
  ${({ buttonStyle, color, theme }) =>
    buttonStyle === 'transparent' &&
    css`
      fill: ${_.get(colorVariables, color, colorVariables[theme.main_default])};
    `};
  // styles for disabled button
  ${({ disable }) =>
    disable &&
    css`
      cursor: default;
    `};
  // styles for active button
  ${({ active, theme }) =>
    active &&
    css`
      fill: ${colorVariables[theme.main_hover_color]};
    `};
`;

export const StyledExpandedQuality = styled.span`
  display: none;
  margin-left: 5px;
  ${embedExpandQuality};
`;

export const StyledExpandedPrice = styled.span`
  display: none;
  text-transform: lowercase;
  margin-left: 5px;
  ${embedExpandPrice};
`;

const Rotate = keyframes`
  from {
    transform: rotate(-42deg);
  }
  to {
    transform: rotate(318deg);
  }
`;

export const Loader = styled.svg`
  position: relative;
  width: 22px;
  height: 22px;
  transform-origin: center center;
  transform: rotate(-42deg);
  animation: ${Rotate} 915ms linear infinite;
`;

export const LoaderCircle = styled.circle`
  stroke-dasharray: 1, 200;
  stroke-dashoffset: 0;
  stroke-linecap: round;
  stroke: white;
  stroke-width: 3px;
`;
